import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Col,
  Descriptions,
  Empty,
  Modal,
  Row,
  Spin,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CrownOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  ShopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import SubscriptionStatusBadge from "./SubscriptionStatusBadge";
import {
  formatSubscriptionAmount,
  formatSubscriptionDate,
  formatSubscriptionNumber,
} from "../subscriptionFormatters";
import "./SubscriptionDetailsModal.css";

const { Text } = Typography;

interface CompanySummary {
  id: number;
  name: string;
  email?: string | null;
  subscription_status?: string;
}

interface SubscriptionTransaction {
  id: number;
  transaction_type: "payment" | "refund" | "upgrade" | "downgrade";
  amount: number | string;
  currency: string;
  payment_method: string | null;
  payment_reference: string | null;
  status: "pending" | "completed" | "failed" | "cancelled";
  notes?: string | null;
  created_at: string;
  processed_at?: string | null;
  plan_name: string | null;
}

interface SubscriptionDetail extends CompanySummary {
  short_name: string | null;
  phone: string | null;
  address?: string | null;
  status: string;
  created_at: string;
  trial_ends_at: string | null;
  licence_expire_on: string | null;
  package_type: string | null;
  subscription_plan_id: number | null;
  subscription_status: "active" | "expired" | "trial" | "suspended";
  total_users: number;
  active_users: number;
  plan_name: string | null;
  plan_description: string | null;
  plan_price: number | null;
  plan_currency: string | null;
  plan_billing_cycle: string | null;
  plan_features: string[];
  plan_max_users: number | null;
  plan_max_companies: number | null;
  plan_storage_limit: number | null;
  recent_transactions: SubscriptionTransaction[];
  commercial_terms?: {
    policy: { collection_mode: "prepaid" | "on_account" };
    pricing: {
      monthly_quote: {
        total_amount: number;
        currency: string;
        billable_warehouse_count: number;
      } | null;
    };
    access: { access_state: string } | null;
  };
}

interface SubscriptionDetailsModalProps {
  open: boolean;
  company: CompanySummary | null;
  onClose: () => void;
}

const transactionLabels: Record<string, string> = {
  payment: "Paiement",
  refund: "Remboursement",
  upgrade: "Montée de gamme",
  downgrade: "Changement vers un plan inférieur",
};

const transactionStatusColors: Record<string, string> = {
  completed: "success",
  pending: "processing",
  failed: "error",
  cancelled: "default",
};

const transactionStatusLabels: Record<string, string> = {
  completed: "Effectuée",
  pending: "En attente",
  failed: "Échouée",
  cancelled: "Annulée",
};

const SubscriptionDetailsModal: React.FC<SubscriptionDetailsModalProps> = ({
  open,
  company,
  onClose,
}) => {
  const [details, setDetails] = useState<SubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDetails = useCallback(
    async (signal?: AbortSignal) => {
      if (!company?.id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/subscription/companies/${company.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            signal,
          },
        );
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(
            data.message || "Impossible de charger les détails de la souscription",
          );
        }

        setDetails(data.data.company);
      } catch (requestError) {
        if ((requestError as Error).name !== "AbortError") {
          setError(
            requestError instanceof Error
              ? requestError.message
              : "Impossible de charger les détails de la souscription",
          );
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [company?.id],
  );

  useEffect(() => {
    if (!open || !company?.id) {
      setDetails(null);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    setDetails(null);
    loadDetails(controller.signal);
    return () => controller.abort();
  }, [company?.id, loadDetails, open]);

  const displayedCompany = details || company;
  const inactiveUsers = details
    ? Math.max(0, Number(details.total_users) - Number(details.active_users))
    : 0;

  const overview = details ? (
    <div className="subscription-details-modal__overview">
      <Row gutter={[12, 12]} className="subscription-details-modal__metrics">
        <Col xs={24} md={8}>
          <div className="subscription-details-modal__metric">
            <span className="subscription-details-modal__metric-icon">
              <CrownOutlined />
            </span>
            <div>
              <span>Plan actuel</span>
              <strong>{details.plan_name || "Aucun plan"}</strong>
              <small>Fonctionnalités et limites</small>
            </div>
          </div>
        </Col>
        <Col xs={24} md={8}>
          <div className="subscription-details-modal__metric">
            <span className="subscription-details-modal__metric-icon">
              <TeamOutlined />
            </span>
            <div>
              <span>Utilisateurs du compte</span>
              <strong>{formatSubscriptionNumber(details.total_users)}</strong>
              <small>
                {formatSubscriptionNumber(details.active_users)} actifs
                {inactiveUsers > 0
                  ? ` · ${formatSubscriptionNumber(inactiveUsers)} désactivés`
                  : ""}
              </small>
            </div>
          </div>
        </Col>
        <Col xs={24} md={8}>
          <div className="subscription-details-modal__metric">
            <span className="subscription-details-modal__metric-icon">
              <CalendarOutlined />
            </span>
            <div>
              <span>Échéance</span>
              <strong>
                {formatSubscriptionDate(
                  details.licence_expire_on || details.trial_ends_at,
                )}
              </strong>
              <small>
                {details.licence_expire_on
                  ? "Expiration de la licence"
                  : details.trial_ends_at
                    ? "Fin de l'essai"
                    : "Aucune date définie"}
              </small>
            </div>
          </div>
        </Col>
      </Row>

      <div className="subscription-details-modal__section">
        <h3>Informations de l'entreprise</h3>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small" colon={false}>
          <Descriptions.Item label="Nom légal">
            {details.name}
          </Descriptions.Item>
          <Descriptions.Item label="Nom court">
            {details.short_name || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="E-mail">
            {details.email ? (
              <span className="subscription-details-modal__inline-value">
                <MailOutlined /> {details.email}
              </span>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Téléphone">
            {details.phone ? (
              <span className="subscription-details-modal__inline-value">
                <PhoneOutlined /> {details.phone}
              </span>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Création du compte">
            {formatSubscriptionDate(details.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            <SubscriptionStatusBadge status={details.subscription_status} />
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="subscription-details-modal__section">
        <h3>Paramètres du plan</h3>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small" colon={false}>
          <Descriptions.Item label="Modèle de facturation">
            Par Warehouse actif
          </Descriptions.Item>
          <Descriptions.Item label="Mensuel estimé">
            {details.commercial_terms?.pricing.monthly_quote
              ? `${formatSubscriptionAmount(
                  details.commercial_terms.pricing.monthly_quote.total_amount,
                  details.commercial_terms.pricing.monthly_quote.currency,
                )} pour ${details.commercial_terms.pricing.monthly_quote.billable_warehouse_count} Warehouse(s)`
              : "Non calculable"}
          </Descriptions.Item>
          <Descriptions.Item label="Utilisateurs autorisés">
            {details.plan_max_users
              ? formatSubscriptionNumber(details.plan_max_users)
              : "Illimité"}
          </Descriptions.Item>
          <Descriptions.Item label="Entreprises autorisées">
            {details.plan_max_companies
              ? formatSubscriptionNumber(details.plan_max_companies)
              : "Illimité"}
          </Descriptions.Item>
          <Descriptions.Item label="Stockage">
            {details.plan_storage_limit
              ? `${formatSubscriptionNumber(details.plan_storage_limit)} Go`
              : "Illimité"}
          </Descriptions.Item>
        </Descriptions>

        {details.plan_description && (
          <Text className="subscription-details-modal__plan-description">
            {details.plan_description}
          </Text>
        )}

        <div className="subscription-details-modal__features">
          {details.plan_features?.length > 0 ? (
            details.plan_features.map((feature, index) => (
              <Tag key={`${feature}-${index}`}>{feature}</Tag>
            ))
          ) : (
            <Text type="secondary">Aucune fonctionnalité détaillée.</Text>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const history = useMemo(() => {
    if (!details) return null;

    if (!details.recent_transactions?.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Aucun mouvement de souscription enregistré."
        />
      );
    }

    return (
      <Timeline
        className="subscription-details-modal__timeline"
        items={details.recent_transactions.map((transaction) => ({
          color:
            transaction.status === "completed"
              ? "green"
              : transaction.status === "failed"
                ? "red"
                : "blue",
          children: (
            <div className="subscription-details-modal__transaction">
              <div className="subscription-details-modal__transaction-heading">
                <strong>
                  {transactionLabels[transaction.transaction_type] ||
                    transaction.transaction_type}
                </strong>
                <Tag color={transactionStatusColors[transaction.status]}>
                  {transactionStatusLabels[transaction.status] ||
                    transaction.status}
                </Tag>
              </div>
              <span>
                {transaction.plan_name || "Plan non renseigné"} · {" "}
                {formatSubscriptionAmount(
                  transaction.amount,
                  transaction.currency,
                )}
              </span>
              <small>
                {formatSubscriptionDate(
                  transaction.processed_at || transaction.created_at,
                )}
                {transaction.payment_method
                  ? ` · ${transaction.payment_method}`
                  : ""}
              </small>
              {transaction.notes && <Text>{transaction.notes}</Text>}
            </div>
          ),
        }))}
      />
    );
  }, [details]);

  const title = (
    <div className="subscription-details-modal__header">
      <Avatar size={52} icon={<ShopOutlined />} />
      <div>
        <div className="subscription-details-modal__heading">
          Détails de la souscription
        </div>
        <div className="subscription-details-modal__subtitle">
          {displayedCompany?.name || "Entreprise"}
          {displayedCompany?.email ? ` · ${displayedCompany.email}` : ""}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      centered
      className="subscription-details-modal"
      title={title}
      open={open}
      onCancel={onClose}
      width={920}
      destroyOnClose
      footer={
        <div className="subscription-details-modal__footer">
          <Button onClick={onClose}>Fermer</Button>
        </div>
      }
    >
      <Spin spinning={loading} tip="Chargement des détails…">
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Détails indisponibles"
            description={error}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => loadDetails()}
              >
                Réessayer
              </Button>
            }
          />
        ) : details ? (
          <Tabs
            items={[
              {
                key: "overview",
                label: (
                  <span>
                    <ShopOutlined /> Vue d'ensemble
                  </span>
                ),
                children: overview,
              },
              {
                key: "history",
                label: (
                  <span>
                    <FileTextOutlined /> Historique récent
                  </span>
                ),
                children: history,
              },
            ]}
          />
        ) : (
          <div className="subscription-details-modal__loading-placeholder" />
        )}
      </Spin>
    </Modal>
  );
};

export default SubscriptionDetailsModal;
